import React, { useCallback, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from '@tanstack/react-router';
import { SubmitHandler } from 'react-hook-form';
import { DocumentRequestDetailQueryResponse } from '@area-restrita/api-client/model/documentRequestDetailQueryResponse';
import { useUpdateDocumentRequest } from '@area-restrita/api-client/endpoints/document-request/document-request.gen';
import {
  companyNextStatusMap,
  DocumentRequestReason,
  DocumentRequestStatus,
  reasonsMap,
  statusMap,
} from '@area-restrita/shared/document/document-requests';

import { useAuthSessionStore } from '@/modules/authentication/stores/authSessionStore';
import PageContainer from '@/components/layout/PageContainer';
import { Alert, AlertDescription } from '@/components/ui/alert';

import CompanyDocumentRequestForm from '../../components/company-document-request-form';
import { CompanyDocumentRequestSchema } from '../../components/company-document-request-form/schema';

interface IProps {
  documentRequest: DocumentRequestDetailQueryResponse;
}

const CompanyDocumentRequestDetails: React.FC<IProps> = ({
  documentRequest,
}) => {
  const navigate = useNavigate();
  const { checkAuthorization } = useAuthSessionStore();

  const canUpdate = checkAuthorization(({ can }) =>
    can('update', 'document_requests'),
  );

  const defaultValues = useMemo(
    () => ({
      hasUploadedFile: documentRequest.hasUploadedFile,
      justification: documentRequest.justification,

      status: statusMap[documentRequest.status],
      document: {
        label: documentRequest.document.name,
        value: documentRequest.document.publicId,
      },
      reason: reasonsMap[documentRequest.reason as DocumentRequestReason],
      reasonFields: documentRequest.reasonFields as Record<string, string>,
      dateForApproval: format(
        parseISO(documentRequest.implantationDate),
        'yyyy-MM-dd',
      ),
      observation: documentRequest.observation,
    }),
    [documentRequest],
  );

  const hasNextStatus = useMemo(
    () => companyNextStatusMap[defaultValues.status.value].length > 0,
    [defaultValues.status.value],
  );

  const inRequestStatus = useMemo(
    () => defaultValues.status.value === 'request',
    [defaultValues.status.value],
  );

  const { mutateAsync } = useUpdateDocumentRequest({
    mutation: {
      onSuccess: () => {
        toast.success('Solicitação de documento atualizada com sucesso');
        navigate({ to: '/my-document-requests' });
      },
      onError: () => {
        toast.error('Erro ao atualizar a solicitação de documento');
      },
    },
  });

  const handlerSubmit = useCallback<
    SubmitHandler<CompanyDocumentRequestSchema>
  >(
    ({ status, reason, dateForApproval, ...data }) =>
      mutateAsync({
        id: documentRequest.id,
        data: {
          status: status.value as DocumentRequestStatus,
          reason: reason.value,
          implantationDate: dateForApproval,
          ...data,
        },
      }),
    [mutateAsync, documentRequest.id],
  );

  const canSave = canUpdate && (hasNextStatus || inRequestStatus);

  return (
    <PageContainer
      title="Detalhes da solicitação de documento"
      subtitle={`Solicitação criada em ${format(
        parseISO(documentRequest.createdAt),
        'dd/MM/yyyy',
      )}`}
    >
      {!hasNextStatus && !inRequestStatus && canUpdate && (
        <Alert className="mb-6 border-amber-500 bg-amber-50 text-amber-800">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="ml-2">
            Esta solicitação não pode ser alterada, já está em andamento. Caso
            seja necessário, favor entrar em contato.
          </AlertDescription>
        </Alert>
      )}

      {!canUpdate && (
        <Alert className="mb-6 border-red-500 bg-red-50 text-red-800">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="ml-2">
            Você não tem permissão para editar esta solicitação. Entre em
            contato com o administrador do sistema para mais informações.
          </AlertDescription>
        </Alert>
      )}

      <CompanyDocumentRequestForm
        onSubmit={handlerSubmit}
        defaultValues={defaultValues}
        documentRequestId={documentRequest.id}
        readOnly={!inRequestStatus}
        canSave={canSave}
      />
    </PageContainer>
  );
};

export default CompanyDocumentRequestDetails;
